import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { BarChart3, LineChart, Hash, Table2, Search, Info, X, GripVertical, ChevronDown } from "lucide-react";
import MetricBlock from "./MetricBlock";
import FunnelQueryConfig from "./FunnelQueryConfig";
import FlowQueryConfig from "./FlowQueryConfig";
import FlowBreakdownControls from "./FlowBreakdownControls";
import RetentionQueryConfig from "./RetentionQueryConfig";
import EventPropertyFilterModule from "features/activity/components/EventPropertyFilterModule";
import { EventPropertyFilterChip, FilterChipOverflowMenu } from "features/activity/components/EventsFilterModule";
import GlobalPropertyFilterModule from "features/activity/components/GlobalPropertyFilterModule";

const DEFAULT_CHART_THEME_ID = "trodo-default";

// Theme palettes (name + array of hex colors for strip)
// Trodo Default: brand primary first (see shared/theme/trodoColors.js).
const CHART_THEMES = [
  { id: "trodo-default", label: "Trodo Default", colors: ["var(--brand-primary)", "#a78bfa", "#f97316", "#14b8a6", "#eab308", "#ec4899", "#6366f1"] },
  { id: "purple", label: "Purple", colors: ["var(--brand-primary)", "#7c3aed", "#a78bfa", "#c4b5fd", "#ddd6fe"] },
  { id: "lava", label: "Lava", colors: ["var(--feedback-error)", "#ea580c", "#f97316", "#fbbf24", "#78716c"] },
  { id: "mint", label: "Mint", colors: ["#14b8a6", "#2dd4bf", "#5eead4", "#99f6e4", "#ccfbf1"] },
  { id: "deuteranopia", label: "Deuteranopia", colors: ["var(--feedback-info)", "var(--feedback-success)", "#eab308", "#f97316"] },
  { id: "protanopia", label: "Protanopia", colors: ["#eab308", "#a16207", "var(--feedback-error)", "#a78bfa"] },
  { id: "tritanopia", label: "Tritanopia", colors: ["var(--feedback-info)", "var(--feedback-success)", "#ec4899", "var(--feedback-warning)"] },
  { id: "focus", label: "Focus", colors: ["#f97316", "var(--text-muted)", "var(--text-secondary)", "#334155", "#1e293b"] },
  { id: "test", label: "test", colors: ["#ec4899", "#14b8a6", "#eab308", "#a78bfa", "#f97316"] },
];

/** Maps legacy persisted theme ids to the current default when unknown. */
function normalizeChartThemeId(id) {
  if (!id) return DEFAULT_CHART_THEME_ID;
  if (id === "mixpanel-default") return DEFAULT_CHART_THEME_ID;
  return CHART_THEMES.some((t) => t.id === id) ? id : DEFAULT_CHART_THEME_ID;
}

const CHART_TYPES = [
  { id: "line", label: "Line", icon: LineChart },
  { id: "bar", label: "Bar", icon: BarChart3 },
  { id: "metric", label: "Metric", icon: Hash },
  { id: "table", label: "Table", icon: Table2 },
];

export default function QueryBuilderPanel({
  activeTab = "query",
  onTabChange,
  reportType = "insight", // 'insight' | 'funnel' | 'flow' | 'retention' - Query tab shows appropriate config
  // Funnel-specific (when reportType === 'funnel')
  funnelSteps = [],
  onFunnelStepsChange = null,
  // Flow-specific (when reportType === 'flow')
  flowAnchorEvents = [],
  onFlowAnchorEventsChange = null,
  flowConversionWindow = { value: 7, unit: 'days' },
  onFlowConversionWindowChange = null,
  flowConversionCriteria = 'distinct_id',
  onFlowConversionCriteriaChange = null,
  // Retention-specific (when reportType === 'retention')
  retentionStartEvent = "",
  onRetentionStartEventChange = null,
  retentionReturnEvent = "",
  onRetentionReturnEventChange = null,
  retentionInterval = "day",
  onRetentionIntervalChange = null,
  retentionRule = "on_or_after",
  onRetentionRuleChange = null,
  retentionMetric = "retention_rate",
  onRetentionMetricChange = null,
  retentionCountSubject = "user",
  onRetentionCountSubjectChange = null,
  retentionAccumulationGroupKey = null,
  onRetentionAccumulationGroupKeyChange = null,
  /** Column labels from last retention query (Chart tab / line view interval picker) */
  retentionIntervals = [],
  // Legacy props (for backward compatibility)
  selectedEvent,
  onEventSelect,
  selectedAggregation = "count",
  onAggregationSelect,
  // New props for multi-event metrics
  metrics = null,
  onMetricsChange = null,
  availableEvents = [],
  chartType = "line",
  onChartTypeChange,
  teamId = null,
  breakdown = null, // Legacy: single breakdown (for backward compatibility)
  onBreakdownChange = null, // Legacy
  breakdowns = [], // New: array of breakdowns
  onBreakdownsChange = null, // New: handler for breakdowns array
  visualizationConfig = null,
  onVisualizationConfigChange = null,
  // Legacy filter props (deprecated, filters are now per-event)
  metricFilters = [],
  onMetricFiltersChange,
  // Global filters props
  globalFilters = [],
  onGlobalFiltersChange = null,
  // Funnel measuring props
  funnelMeasuring = "conversion_rate",
  onFunnelMeasuringChange = null,
  funnelMeasuringProperty = null,
  onFunnelMeasuringPropertyChange = null,
  funnelMeasuringAggregation = "average",
  onFunnelMeasuringAggregationChange = null,
  funnelMeasuringPercentileValue = 50,
  onFunnelMeasuringPercentileValueChange = null,
  funnelConversionWindow = { value: 7, unit: 'days' },
  onFunnelConversionWindowChange = null,
  funnelAnyOrder = false,
  onFunnelAnyOrderChange = null,
  funnelCountSubject = "user",
  onFunnelCountSubjectChange = null,
  funnelAccumulationGroupKey = null,
  onFunnelAccumulationGroupKeyChange = null,
  flowBreakdown = { mode: "none", cohortIds: [] },
  onFlowBreakdownChange = null,
}) {
  // Compact bordered select (e.g. retention breakdown step)
  function InlineDropdown({
    value,
    options = [],
    onChange,
    disabled = false,
    widthClass = "min-w-[120px]",
  }) {
    const [open, setOpen] = React.useState(false);
    const menuRef = React.useRef(null);
    const buttonRef = React.useRef(null);
    const selected = options.find((o) => String(o.value) === String(value)) || options[0];

    React.useEffect(() => {
      const handleClickOutside = (e) => {
        if (!open) return;
        if (menuRef.current && menuRef.current.contains(e.target)) return;
        if (buttonRef.current && buttonRef.current.contains(e.target)) return;
        setOpen(false);
      };
      const handleEsc = (e) => {
        if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEsc);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEsc);
      };
    }, [open]);

    return (
      <div className={`relative ${widthClass}`}>
        <button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen((v) => !v)}
          className="w-full h-8 px-2 text-[13px] border rounded-md flex items-center justify-between gap-2"
          style={{
            backgroundColor: "var(--surface-base)",
            borderColor: "var(--border-default)",
            color: "var(--text-primary)",
            opacity: disabled ? 0.6 : 1,
          }}
        >
          <span className="truncate text-left">{selected?.label ?? ""}</span>
          <span style={{ color: "var(--text-muted)" }}>▾</span>
        </button>
        {open && !disabled && (
          <div
            ref={menuRef}
            className="absolute left-0 right-0 mt-1 z-[120] border rounded-lg overflow-hidden shadow-token-lg"
            style={{
              backgroundColor: "var(--surface-base)",
              borderColor: "var(--border-default)",
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            {options.map((opt) => {
              const active = String(opt.value) === String(value);
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => {
                    onChange?.(opt.value);
                    setOpen(false);
                  }}
                  className="w-full px-2 py-1.5 text-[13px] text-left transition-colors"
                  style={{
                    backgroundColor: active ? "var(--surface-sunken)" : "transparent",
                    color: active ? "var(--text-primary)" : "var(--text-secondary)",
                    borderLeft: active ? `2px solid var(--brand-primary)` : "2px solid transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  const isFunnel = reportType === "funnel";
  const isFlow = reportType === "flow";
  const isRetention = reportType === "retention";

  const defaultLeftY = { visible: true, label: "", unit: "#", customUnit: "", abbreviation: true, decimals: ".0", thresholdMin: "", thresholdMax: "", outOfBounds: "hidden", scale: "linear" };
  const defaultRightY = { visible: true, label: "", unit: "#", customUnit: "", abbreviation: true, decimals: ".0", thresholdMin: "", thresholdMax: "", outOfBounds: "hidden", scale: "linear" };
  const defaultXAxis = { visible: true, label: "" };

  // Use new metrics array if provided, otherwise create from legacy props
  const [internalMetrics, setInternalMetrics] = useState(() => {
    if (metrics && Array.isArray(metrics)) {
      return metrics;
    }
    // Legacy: convert single event to metric format
    if (selectedEvent) {
      return [{
        id: 'm1',
        aggregation: selectedAggregation,
        axis: 'left',
        countSubject: 'user',
        events: [{
          name: selectedEvent,
          filters: metricFilters || []
        }]
      }];
    }
    return [];
  });

  const currentMetrics = metrics || internalMetrics;
  const handleMetricsChange = onMetricsChange || setInternalMetrics;
  // Axis state (Left Y, Right Y, X) - initialize from visualizationConfig when provided
  const [leftY, setLeftY] = useState(() => {
    const axis = visualizationConfig?.axis;
    return axis?.leftY ? { ...defaultLeftY, ...axis.leftY } : defaultLeftY;
  });
  const [rightY, setRightY] = useState(() => {
    const axis = visualizationConfig?.axis;
    return axis?.rightY ? { ...defaultRightY, ...axis.rightY } : defaultRightY;
  });
  const [xAxis, setXAxis] = useState(() => {
    const axis = visualizationConfig?.axis;
    return axis?.xAxis ? { ...defaultXAxis, ...axis.xAxis } : defaultXAxis;
  });
  const [selectedThemeId, setSelectedThemeId] = useState(() =>
    normalizeChartThemeId(visualizationConfig?.themeId) || DEFAULT_CHART_THEME_ID
  );
  const [themeSearch, setThemeSearch] = useState("");
  const [funnelAxis, setFunnelAxis] = useState(() => visualizationConfig?.funnelAxis ?? "left");
  const [retentionLineIntervalIndex, setRetentionLineIntervalIndex] = useState(() =>
    typeof visualizationConfig?.retentionLineIntervalIndex === "number" &&
    visualizationConfig.retentionLineIntervalIndex >= 0
      ? visualizationConfig.retentionLineIntervalIndex
      : 0
  );

  // Sync external visualizationConfig into internal state when loading a saved card
  const lastAppliedConfigRef = useRef(null);
  const skipNextSyncToParentRef = useRef(false);
  useEffect(() => {
    if (!visualizationConfig || typeof visualizationConfig !== "object") return;
    // When config changes (e.g. saved config arrives after defaults), re-apply it
    const configChanged = lastAppliedConfigRef.current !== visualizationConfig;
    if (configChanged) lastAppliedConfigRef.current = visualizationConfig;
    if (!configChanged) return;
    const axis = visualizationConfig.axis;
    const hasAxis = axis && typeof axis === "object";
    skipNextSyncToParentRef.current = true; // Don't overwrite parent with our state during apply
    if (hasAxis) {
      if (axis.leftY) setLeftY((prev) => ({ ...defaultLeftY, ...prev, ...axis.leftY }));
      if (axis.rightY) setRightY((prev) => ({ ...defaultRightY, ...prev, ...axis.rightY }));
      if (axis.xAxis) setXAxis((prev) => ({ ...defaultXAxis, ...prev, ...axis.xAxis }));
    }
    if (visualizationConfig.themeId) setSelectedThemeId(normalizeChartThemeId(visualizationConfig.themeId));
    if (visualizationConfig.funnelAxis) setFunnelAxis(visualizationConfig.funnelAxis);
    if (
      isRetention &&
      typeof visualizationConfig.retentionLineIntervalIndex === "number" &&
      visualizationConfig.retentionLineIntervalIndex >= 0
    ) {
      setRetentionLineIntervalIndex(visualizationConfig.retentionLineIntervalIndex);
    }
  }, [visualizationConfig, isRetention]);

  // Sync axis/theme to parent for persistence when they change (include theme colors for board render)
  useEffect(() => {
    if (typeof onVisualizationConfigChange !== "function") return;
    if (skipNextSyncToParentRef.current) {
      skipNextSyncToParentRef.current = false;
      return;
    }
    const resolvedThemeId = normalizeChartThemeId(selectedThemeId);
    const theme = CHART_THEMES.find((t) => t.id === resolvedThemeId) || CHART_THEMES[0];
    onVisualizationConfigChange({
      axis: { leftY, rightY, xAxis },
      themeId: resolvedThemeId,
      colors: theme?.colors || undefined,
      ...(isFunnel ? { funnelAxis } : {}),
      ...(isRetention ? { retentionLineIntervalIndex } : {}),
    });
  }, [leftY, rightY, xAxis, selectedThemeId, funnelAxis, isFunnel, isRetention, retentionLineIntervalIndex, onVisualizationConfigChange]);

  /** After adding a metric, open its event picker once (avoids Add metric → Add event → picker). */
  const [openEventPickerForMetricIndex, setOpenEventPickerForMetricIndex] = useState(null);
  const clearOpenEventPickerRequest = useCallback(() => {
    setOpenEventPickerForMetricIndex(null);
  }, []);

  // Metric management functions
  const handleAddMetric = ({ openEventPicker = true } = {}) => {
    // Generate unique ID based on existing metric IDs to avoid conflicts
    const existingIds = currentMetrics.map(m => m.id).filter(Boolean);
    let newId = `m${currentMetrics.length + 1}`;
    let counter = currentMetrics.length + 1;
    // Ensure ID is unique
    while (existingIds.includes(newId)) {
      counter++;
      newId = `m${counter}`;
    }
    
    const newMetric = {
      id: newId,
      aggregation: 'count',
      axis: 'left',
      countSubject: 'user',
      events: []
    };
    const newIndex = currentMetrics.length;
    handleMetricsChange([...currentMetrics, newMetric]);
    if (openEventPicker) {
      setOpenEventPickerForMetricIndex(newIndex);
    }
  };

  const handleUpdateMetric = (metricIndex, updates) => {
    const updated = [...currentMetrics];
    updated[metricIndex] = { ...updated[metricIndex], ...updates };
    handleMetricsChange(updated);
  };

  const handleRemoveMetric = (metricIndex) => {
    const updated = currentMetrics.filter((_, i) => i !== metricIndex);
    // Note: We keep existing IDs even after removal to maintain consistency
    // This is fine as long as new metrics get unique IDs
    handleMetricsChange(updated);
  };

  const handleAddEvent = (metricIndex, eventName, kindOrSource = 'event', opts = {}) => {
    const updated = [...currentMetrics];
    const metric = updated[metricIndex];
    // The selector emits kind ∈ { 'event' | 'agent' | 'span' }. Anything
    // else is a legacy `source` value (e.g. 'transaction'); we keep it on
    // `source` for back-compat but map it to entityKind='event' for the
    // new compiler dispatch.
    const newKind = (kindOrSource === 'agent' || kindOrSource === 'span') ? kindOrSource : 'event';
    const legacySource = (newKind === 'event') ? (kindOrSource || 'event') : 'event';
    const spanKind = newKind === 'span' ? (opts && opts.spanKind) || null : null;

    // For agent / span entities, lock the metric aggregation to count.
    if (newKind === 'agent' || newKind === 'span') {
      metric.aggregation = 'count';
    }

    const key = (e) => `${e.name}\0${e.entityKind || e.source || 'event'}\0${e.spanKind || ''}`;
    const eventKey = `${eventName}\0${newKind}\0${spanKind || ''}`;
    if (!metric.events.some((e) => key(e) === eventKey)) {
      metric.events.push({
        name: eventName,
        source: legacySource,
        entityKind: newKind,
        spanKind,
        filters: [],
      });
      handleMetricsChange(updated);
    }
  };

  const handleRemoveEvent = (metricIndex, eventIndex) => {
    const updated = [...currentMetrics];
    updated[metricIndex].events = updated[metricIndex].events.filter((_, i) => i !== eventIndex);
    handleMetricsChange(updated);
  };

  const handleUpdateEventFilters = (metricIndex, eventIndex, filters) => {
    const updated = [...currentMetrics];
    updated[metricIndex].events[eventIndex].filters = filters;
    handleMetricsChange(updated);
  };

  const globalCohortFilters = globalFilters.filter(f =>
    f.category === 'cohort' || f.propertySource === 'cohort'
  );
  const globalEventFilters = globalFilters.filter(f =>
    f.category !== 'transaction_properties' &&
    f.propertySource !== 'transaction' &&
    f.category !== 'first_time' &&
    f.category !== 'cohort' &&
    f.propertySource !== 'cohort'
  );
  const globalPropertyFilters = globalEventFilters;
  const globalFirstTimeFilter = globalFilters.find(f => f.category === 'first_time');

  useEffect(() => {
    if (!onGlobalFiltersChange) return;
    const next = (globalFilters || []).filter(
      (f) => f.category !== 'transaction_properties' && f.propertySource !== 'transaction'
    );
    if (next.length !== (globalFilters || []).length) {
      onGlobalFiltersChange(next);
    }
  }, [globalFilters, onGlobalFiltersChange]);

  // Global filter state and handlers
  const [isGlobalFilterOpen, setIsGlobalFilterOpen] = useState(false);
  const [selectedGlobalFilterForEdit, setSelectedGlobalFilterForEdit] = useState(null);
  const globalFilterButtonRef = useRef(null);
  const globalFilterChipRefs = useRef({});

  // Breakdown state and handlers
  const [breakdownPickerOpen, setBreakdownPickerOpen] = useState(false);
  const [breakdownPickerIndex, setBreakdownPickerIndex] = useState(null);
  const breakdownPickerRef = useRef(null);
  const breakdownPickerIndexRef = useRef(null);
  breakdownPickerIndexRef.current = breakdownPickerIndex;
  const breakdownRowElsRef = useRef({});
  const breakdownPopupTriggerRef = useMemo(
    () => ({
      get current() {
        const idx = breakdownPickerIndexRef.current;
        if (idx !== null && idx !== undefined && breakdownRowElsRef.current[idx]) {
          return breakdownRowElsRef.current[idx];
        }
        return breakdownPickerRef.current;
      },
    }),
    []
  );
  
  // Use breakdowns array if provided, otherwise convert single breakdown to array for backward compatibility
  const currentBreakdowns = breakdowns && Array.isArray(breakdowns) 
    ? breakdowns 
    : (breakdown ? [breakdown] : []);
  
  const handleBreakdownsChange = (newBreakdowns) => {
    if (onBreakdownsChange) {
      onBreakdownsChange(newBreakdowns);
    } else if (onBreakdownChange) {
      // Legacy: convert array back to single breakdown
      onBreakdownChange(newBreakdowns.length > 0 ? newBreakdowns[0] : null);
    }
  };

  const handleGlobalFilterClick = () => {
    setSelectedGlobalFilterForEdit(null);
    setIsGlobalFilterOpen(true);
  };

  const handleGlobalFiltersChange = (newFilter) => {
    if (selectedGlobalFilterForEdit) {
      // Update existing filter — preserve category/propertySource
      const existing = globalFilters.find(f => f.id === selectedGlobalFilterForEdit.id);
      const merged = existing ? {
        ...existing,
        ...newFilter,
        id: existing.id,
        category: newFilter.category ?? existing.category,
        propertySource: newFilter.propertySource ?? existing.propertySource,
        field: newFilter.field ?? newFilter.property ?? existing.field ?? existing.property,
        property: newFilter.property ?? newFilter.field ?? existing.property ?? existing.field,
      } : newFilter;
      const updated = globalFilters.map(f =>
        f.id === selectedGlobalFilterForEdit.id ? merged : f
      );
      onGlobalFiltersChange && onGlobalFiltersChange(updated);
      setSelectedGlobalFilterForEdit(null);
    } else {
      // Add new filter coming from global popup
      let filterWithId;

      // Special handling for first_time filter - ensure it has the correct structure
      if (newFilter.category === 'first_time') {
        filterWithId = {
          id: newFilter.id || `global-first-time-${Date.now()}`,
          field: 'firstTime',
          property: 'firstTime',
          operator: 'equals', // Required for query compiler
          value: true,
          category: 'first_time',
          connector: globalFilters.length === 0 ? 'WHERE' : 'AND'
        };
      } else {
        const propName = (newFilter.property || newFilter.field || '').toString();
        const isCohortFilter = newFilter.propertySource === 'cohort' || newFilter.category === 'cohort';
        const isUserProp =
          newFilter.propertySource === 'user' ||
          newFilter.category === 'user_properties' ||
          (propName && propName.startsWith('user.'));

        if (isCohortFilter) {
          filterWithId = {
            ...newFilter,
            id: newFilter.id || `global-cohort-filter-${Date.now()}-${Math.random()}`,
            category: 'cohort',
            propertySource: 'cohort',
            field: 'cohort',
            property: 'cohort',
            connector: (globalPropertyFilters.length === 0 && globalCohortFilters.length === 0) ? 'WHERE' : 'AND'
          };
        } else if (isUserProp) {
          // Global user property; propertySource stays 'user' for the query compiler.
          filterWithId = {
            ...newFilter,
            id: newFilter.id || `global-user-filter-${Date.now()}-${Math.random()}`,
            category: newFilter.category || 'global',
            propertySource: 'user',
            connector: (globalPropertyFilters.length === 0 && globalCohortFilters.length === 0) ? 'WHERE' : 'AND'
          };
        } else {
          // Event property (global for all event-based metrics)
          filterWithId = {
            ...newFilter,
            id: newFilter.id || `global-filter-${Date.now()}-${Math.random()}`,
            category: newFilter.category || 'global',
            connector: (globalPropertyFilters.length === 0 && globalCohortFilters.length === 0) ? 'WHERE' : 'AND'
          };
        }
      }

      onGlobalFiltersChange && onGlobalFiltersChange([...globalFilters, filterWithId]);
    }
    setIsGlobalFilterOpen(false);
  };

  const handleGlobalFirstTimeChange = (enabled) => {
    if (enabled) {
      // Remove any existing first_time filter first
      const withoutFirstTime = globalFilters.filter(f => f.category !== 'first_time');
      // Add the first time filter with correct structure
      const firstTimeFilter = {
        id: 'global-first-time-filter',
        field: 'firstTime',
        property: 'firstTime',
        operator: 'equals',
        value: true,
        category: 'first_time',
        connector: withoutFirstTime.length === 0 ? 'WHERE' : 'AND'
      };
      onGlobalFiltersChange && onGlobalFiltersChange([...withoutFirstTime, firstTimeFilter]);
    } else {
      // Remove the first time filter
      const withoutFirstTime = globalFilters.filter(f => f.category !== 'first_time');
      const updatedWithConnectors = withoutFirstTime.map((f, idx) => ({
        ...f,
        connector: idx === 0 ? 'WHERE' : 'AND'
      }));
      onGlobalFiltersChange && onGlobalFiltersChange(updatedWithConnectors);
    }
  };

  const handleRemoveGlobalFilter = (filterId) => {
    const updated = globalFilters.filter(f => f.id !== filterId);
    const orderedForConnectors = [
      ...updated.filter((f) => f.category === 'cohort' || f.propertySource === 'cohort'),
      ...updated.filter(
        (f) =>
          f.category !== 'first_time' &&
          f.category !== 'cohort' &&
          f.propertySource !== 'cohort'
      ),
    ];
    const updatedWithConnectors = updated.map((f) => {
      if (f.category === 'first_time') {
        return { ...f, connector: 'AND' };
      }
      const pos = orderedForConnectors.findIndex((x) => x.id === f.id);
      return { ...f, connector: pos === 0 ? 'WHERE' : 'AND' };
    });
    onGlobalFiltersChange && onGlobalFiltersChange(updatedWithConnectors);
  };

  const handleEditGlobalFilter = (filter) => {
    // First time filters are not editable - they can only be toggled
    if (filter.category === 'first_time') {
      return;
    }
    setSelectedGlobalFilterForEdit(filter);
    setIsGlobalFilterOpen(true);
  };

  // Get first event name for global filters (or use null/placeholder)
  const firstEventName = currentMetrics.length > 0 && 
    currentMetrics[0]?.events?.length > 0 
    ? currentMetrics[0].events[0].name 
    : null;

  return (
    <aside
      className="relative flex w-full md:w-[340px] flex-shrink-0 flex-col overflow-hidden border-l-0 md:border-l border-[color:var(--border-default)] bg-[color:var(--surface-sunken)]"
      style={{
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* Extend left border upward to touch top border above header */}
      <div
        className="absolute left-0"
        style={{
          top: "-56px",
          height: "56px",
          width: "1px",
          backgroundColor: "var(--border-default)",
          pointerEvents: "none",
          zIndex: 10,
        }}
      />
      <div className="p-4 overflow-y-auto flex-1 relative">
        {activeTab === "query" && (
          <>
            {/* Query config: Insight = metrics, Funnel = steps, Flow = anchor events, Retention = start/return events */}
            {isFlow ? (
              <FlowQueryConfig
                anchorEvents={flowAnchorEvents}
                onAnchorEventsChange={onFlowAnchorEventsChange || (() => {})}
                conversionWindow={flowConversionWindow}
                onConversionWindowChange={onFlowConversionWindowChange || (() => {})}
                conversionCriteria={flowConversionCriteria}
                onConversionCriteriaChange={onFlowConversionCriteriaChange || (() => {})}
                availableEvents={availableEvents}
                teamId={teamId}
              />
            ) : isRetention ? (
              <RetentionQueryConfig
                startEvent={retentionStartEvent}
                onStartEventChange={onRetentionStartEventChange || (() => {})}
                returnEvent={retentionReturnEvent}
                onReturnEventChange={onRetentionReturnEventChange || (() => {})}
                interval={retentionInterval}
                onIntervalChange={onRetentionIntervalChange || (() => {})}
                retentionRule={retentionRule}
                onRetentionRuleChange={onRetentionRuleChange || (() => {})}
                metric={retentionMetric}
                onMetricChange={onRetentionMetricChange || (() => {})}
                countSubject={retentionCountSubject}
                onCountSubjectChange={onRetentionCountSubjectChange || (() => {})}
                accumulationGroupKey={retentionAccumulationGroupKey}
                onAccumulationGroupKeyChange={onRetentionAccumulationGroupKeyChange || (() => {})}
                chartType={chartType}
                retentionIntervals={retentionIntervals}
                retentionGroupIndex={retentionLineIntervalIndex}
                onRetentionGroupIndexChange={setRetentionLineIntervalIndex}
                availableEvents={availableEvents}
                teamId={teamId}
              />
            ) : isFunnel ? (
              <FunnelQueryConfig
                funnelSteps={funnelSteps}
                onFunnelStepsChange={onFunnelStepsChange || (() => {})}
                availableEvents={availableEvents}
                teamId={teamId}
                funnelMeasuring={funnelMeasuring}
                onFunnelMeasuringChange={onFunnelMeasuringChange}
                funnelMeasuringProperty={funnelMeasuringProperty}
                onFunnelMeasuringPropertyChange={onFunnelMeasuringPropertyChange}
                funnelMeasuringAggregation={funnelMeasuringAggregation}
                onFunnelMeasuringAggregationChange={onFunnelMeasuringAggregationChange}
                funnelMeasuringPercentileValue={funnelMeasuringPercentileValue}
                onFunnelMeasuringPercentileValueChange={onFunnelMeasuringPercentileValueChange}
                funnelConversionWindow={funnelConversionWindow}
                onFunnelConversionWindowChange={onFunnelConversionWindowChange}
                funnelAnyOrder={funnelAnyOrder}
                onFunnelAnyOrderChange={onFunnelAnyOrderChange}
                funnelCountSubject={funnelCountSubject}
                onFunnelCountSubjectChange={onFunnelCountSubjectChange}
                funnelAccumulationGroupKey={funnelAccumulationGroupKey}
                onFunnelAccumulationGroupKeyChange={onFunnelAccumulationGroupKeyChange}
              />
            ) : (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[13px] font-medium" style={{ color: "var(--text-secondary)" }}>
                    Metrics
                  </span>
                  <button
                    type="button"
                    onClick={() => handleAddMetric()}
                    className="text-xl font-medium leading-none transition-colors"
                    style={{ color: "var(--text-secondary)" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "var(--text-primary)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "var(--text-secondary)";
                    }}
                    title="Add metric: event list opens to pick what to measure"
                  >
                    +
                  </button>
                </div>
            
                {/* Render Metrics */}
                {currentMetrics.length === 0 ? (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => handleAddMetric()}
                  className="w-full flex items-center gap-2 px-3 py-2.5 border border-dashed rounded-lg text-left text-sm transition-colors"
                  style={{
                    borderColor: "var(--border-default)",
                    color: "var(--text-muted)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--brand-primary)";
                    e.currentTarget.style.color = "var(--text-primary)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--border-default)";
                    e.currentTarget.style.color = "var(--text-muted)";
                  }}
                >
                  <span
                    className="flex h-5 w-5 flex-shrink-0 items-center justify-center tabular-nums text-[13px] font-medium"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    A
                  </span>
                  <span className="min-w-0 flex-1">Select Metric</span>
                </button>
              </div>
            ) : (
              <div className="mb-6">
                {currentMetrics.map((metric, index) => (
                  <MetricBlock
                    key={metric.id || `metric-${index}`}
                    metric={metric}
                    metricIndex={index}
                    availableEvents={availableEvents}
                    teamId={teamId}
                    onUpdate={handleUpdateMetric}
                    onRemove={handleRemoveMetric}
                    onAddEvent={handleAddEvent}
                    onRemoveEvent={handleRemoveEvent}
                    onUpdateEventFilters={handleUpdateEventFilters}
                    openEventPickerRequest={openEventPickerForMetricIndex === index}
                    onOpenEventPickerRequestConsumed={clearOpenEventPickerRequest}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => handleAddMetric()}
                  className="mt-2 flex w-full items-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-left text-sm transition-colors"
                  style={{
                    borderColor: "var(--border-default)",
                    color: "var(--text-muted)",
                  }}
                  title="Adds a new series and opens the event list"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--brand-primary)";
                    e.currentTarget.style.color = "var(--text-primary)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--border-default)";
                    e.currentTarget.style.color = "var(--text-muted)";
                  }}
                >
                  <span
                    className="flex h-5 w-5 flex-shrink-0 items-center justify-center tabular-nums text-[13px] font-medium"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {String.fromCharCode(65 + currentMetrics.length)}
                  </span>
                  <span className="min-w-0 flex-1">Add Another Metric</span>
                  <ChevronDown className="h-4 w-4 flex-shrink-0" style={{ color: "var(--text-muted)" }} aria-hidden />
                </button>
              </div>
            )}
              </>
            )}

            {/* Global Filters Section */}
            <div className="mb-2 mt-6 flex items-center justify-between">
              <span className="text-sm font-medium text-ink-muted">
                Filter
              </span>
              <button
                type="button"
                className="text-xl font-medium leading-none transition-colors text-ink-muted hover:text-ink-secondary"
                onClick={handleGlobalFilterClick}
                ref={globalFilterButtonRef}
              >
                +
              </button>
            </div>

            <div className="mb-4">
              <div className="mb-2 space-y-2">
                {[...globalCohortFilters, ...globalPropertyFilters].map((filter, index) => {
                  const isCohort = filter.category === 'cohort' || filter.propertySource === 'cohort';
                  const isFirstRegularFilter = index === 0;
                  const ChipComponent = isCohort ? null : EventPropertyFilterChip;
                  return (
                    <div
                      key={filter.id || index}
                      ref={(el) => {
                        if (el) globalFilterChipRefs.current[filter.id || index] = el;
                      }}
                      className="w-full"
                    >
                      {isCohort ? (
                        <div className="flex w-full flex-col gap-1 rounded-lg border border-line bg-surface px-2.5 py-2 shadow-sm">
                          <div className="flex w-full items-start gap-2">
                            <div
                              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-surface-hover text-[11px] font-bold text-ink"
                              title="Cohort"
                            >
                              C
                            </div>
                            <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                              <button
                                type="button"
                                className="min-w-0 flex-1 cursor-pointer text-left leading-snug"
                                onClick={() => handleEditGlobalFilter(filter)}
                              >
                                <span className="text-xs text-ink-muted">Cohort</span>
                                <span className="mx-1 text-ink-disabled" aria-hidden>
                                  ›
                                </span>
                                <span className="text-sm font-semibold text-ink">
                                  {filter.propertyLabel || 'Cohort'}
                                </span>
                              </button>
                              <FilterChipOverflowMenu
                                showTypeSelector={false}
                                onRemove={() => handleRemoveGlobalFilter(filter.id || index)}
                              />
                            </div>
                          </div>
                          <div className="pl-8 text-xs text-ink-secondary">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-disabled">
                              {index === 0 ? 'WHERE' : 'AND'}
                            </span>
                            <span className="ml-1.5 text-ink-muted">In cohort</span>
                          </div>
                        </div>
                      ) : (
                        <ChipComponent
                          filter={filter}
                          compact
                          stackedCard
                          isFirst={isFirstRegularFilter}
                          onEdit={() => handleEditGlobalFilter(filter)}
                          onRemove={() => handleRemoveGlobalFilter(filter.id || index)}
                          onUpdate={(updates) => {
                            const updated = globalFilters.map(f =>
                              f.id === filter.id ? { ...f, ...updates } : f
                            );
                            onGlobalFiltersChange && onGlobalFiltersChange(updated);
                          }}
                          teamId={teamId}
                          eventName={null}
                        />
                      )}
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-left text-sm transition-colors"
                  style={{ borderColor: "var(--border-default)", color: "var(--text-muted)" }}
                  onClick={handleGlobalFilterClick}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "var(--brand-primary)";
                    e.currentTarget.style.color = "var(--text-primary)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "var(--border-default)";
                    e.currentTarget.style.color = "var(--text-muted)";
                  }}
                >
                  <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-ink-muted font-medium">
                    +
                  </span>
                  <span className="min-w-0 flex-1">Filter</span>
                </button>
              </div>
            </div>

            {/* First Time Filter (applies to both) */}
            {globalFirstTimeFilter && (
              <div className="mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-surface-hover rounded-lg">
                    <span className="text-xs font-medium text-ink-secondary">WHERE</span>
                    <span className="text-sm text-ink font-medium">First time only</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveGlobalFilter(globalFirstTimeFilter.id)}
                      className="text-ink-muted hover:text-ink-secondary"
                      title="Remove First time only"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {isFlow ? (
              <>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-ink-muted">Breakdown</span>
                </div>
                <FlowBreakdownControls
                  flowBreakdown={flowBreakdown}
                  onFlowBreakdownChange={onFlowBreakdownChange || (() => {})}
                  teamId={teamId}
                />
              </>
            ) : (
              <>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-ink-muted">
                Breakdown
              </span>
              <button
                type="button"
                className="text-xl font-medium leading-none transition-colors text-ink-muted hover:text-ink-secondary"
                title="Add breakdown"
                onClick={() => {
                  setBreakdownPickerIndex(null);
                  setBreakdownPickerOpen(true);
                }}
              >
                +
              </button>
            </div>
            
            {/* Existing breakdowns list */}
            {currentBreakdowns.length > 0 && (
              <div className="space-y-2 mb-3">
                {currentBreakdowns.map((bd, index) => (
                  <div
                    key={index}
                    ref={(el) => {
                      if (el) breakdownRowElsRef.current[index] = el;
                      else delete breakdownRowElsRef.current[index];
                    }}
                    className="group px-3 py-2.5 border rounded-lg text-sm flex items-center gap-2"
                    style={{ borderColor: "var(--border-default)" }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', index.toString());
                      e.currentTarget.style.opacity = '0.5';
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
                      if (dragIndex === index) return;
                      
                      const newBreakdowns = [...currentBreakdowns];
                      const [removed] = newBreakdowns.splice(dragIndex, 1);
                      newBreakdowns.splice(index, 0, removed);
                      handleBreakdownsChange(newBreakdowns);
                    }}
                  >
                    <div className="cursor-move text-ink-disabled hover:text-ink-secondary flex-shrink-0" title="Drag to reorder">
                      <GripVertical className="h-4 w-4" />
                    </div>

                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-2 text-left rounded-md outline-none ring-[color:var(--trodo-primary)] focus-visible:ring-2"
                      style={{ color: "var(--text-primary)" }}
                      onClick={() => {
                        setBreakdownPickerIndex(index);
                        setBreakdownPickerOpen(true);
                      }}
                    >
                      <span
                        className="font-medium truncate"
                        title={bd.propertyLabel || bd.property || 'Unnamed property'}
                      >
                        {bd.propertyLabel || bd.property || 'Unnamed property'}
                      </span>
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-surface-hover text-ink-secondary flex-shrink-0"
                        title={
                          bd.propertySource === 'cohort' || bd.category === 'cohort'
                            ? 'Cohort'
                            : bd.propertySource === 'user'
                              ? 'User property'
                              : bd.propertySource && ['agent_call','agent_tool','agent_error','agent_response','agent_feedback'].includes(bd.propertySource)
                                ? 'Agent property'
                                : 'Event property'
                        }
                      >
                        {bd.propertySource === 'cohort' || bd.category === 'cohort'
                          ? 'C'
                          : bd.propertySource === 'user'
                            ? 'U'
                            : bd.propertySource && ['agent_call','agent_tool','agent_error','agent_response','agent_feedback'].includes(bd.propertySource)
                              ? 'A'
                              : 'E'}
                      </span>
                      
                      {!isRetention && (
                        <span className="text-xs flex-shrink-0" style={{ color: "var(--text-muted)" }}>
                          (Up to 50)
                        </span>
                      )}
                    </button>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Edit button */}
                      <button
                        type="button"
                        onClick={() => {
                          setBreakdownPickerIndex(index);
                          setBreakdownPickerOpen(true);
                        }}
                        className="text-xs px-2 py-1 rounded transition-colors"
                        style={{
                          backgroundColor: "var(--surface-base)",
                          color: "var(--text-primary)"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "var(--surface-hover)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "var(--surface-base)";
                        }}
                      >
                        Edit
                      </button>
                      
                      {/* Remove button */}
                      <button
                        type="button"
                        onClick={() => {
                          const newBreakdowns = currentBreakdowns.filter((_, i) => i !== index);
                          handleBreakdownsChange(newBreakdowns);
                        }}
                        className="text-xs text-danger hover:text-danger transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add breakdown button (anchor for breakdown picker when adding) */}
            <button
              ref={breakdownPickerRef}
              type="button"
              onClick={() => {
                setBreakdownPickerIndex(null);
                setBreakdownPickerOpen(true);
              }}
              className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-dashed px-3 py-2.5 text-left text-sm transition-colors"
              style={{ borderColor: "var(--border-default)", color: "var(--text-muted)" }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--brand-primary)";
                e.currentTarget.style.color = "var(--text-primary)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--border-default)";
                e.currentTarget.style.color = "var(--text-muted)";
              }}
            >
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center text-ink-muted font-medium">
                +
              </span>
              <span className="min-w-0 flex-1">Add Breakdown</span>
            </button>
            
            {/* GlobalPropertyFilterModule for breakdown selection */}
            <GlobalPropertyFilterModule
              isOpen={breakdownPickerOpen}
              onClose={() => {
                setBreakdownPickerOpen(false);
                setBreakdownPickerIndex(null);
              }}
              triggerRef={breakdownPopupTriggerRef}
              teamId={teamId}
              isBreakdownPicker={true}
              existingFilter={breakdownPickerIndex !== null ? currentBreakdowns[breakdownPickerIndex] : null}
              onGlobalFilterAdd={(filter) => {
                // Convert filter to breakdown format
                // Cohort as breakdown = break by all cohorts (no value); like Country - user does not pick each cohort
                const isRetentionBreakdown = isRetention === true;
                const newBreakdown = {
                  property: filter.property || filter.field,
                  propertyLabel: filter.propertyLabel || filter.property || filter.field || 'Unnamed property',
                  propertySource: filter.propertySource || filter.category || 'event',
                  limit: filter.limit || currentBreakdowns[breakdownPickerIndex]?.limit || 10,
                  // Do NOT pass value for cohort breakdown - backend uses all cohorts
                  ...((filter.propertySource === 'cohort' || filter.category === 'cohort') && { value: undefined }),
                  ...(isRetentionBreakdown ? {
                    step: currentBreakdowns[breakdownPickerIndex]?.step ?? 1,
                    // Fetch up to 50 segments from API; chart top-N is controlled in the table header.
                    limit:
                      breakdownPickerIndex !== null &&
                      typeof currentBreakdowns[breakdownPickerIndex]?.limit === "number"
                        ? currentBreakdowns[breakdownPickerIndex].limit
                        : 50,
                    showMax:
                      breakdownPickerIndex !== null
                        ? currentBreakdowns[breakdownPickerIndex]?.showMax === true
                        : true,
                    sortOrder: currentBreakdowns[breakdownPickerIndex]?.sortOrder ?? "value-desc",
                    includeOther: currentBreakdowns[breakdownPickerIndex]?.includeOther !== false,
                  } : {}),
                };
                
                if (breakdownPickerIndex !== null) {
                  // Editing existing breakdown
                  const newBreakdowns = [...currentBreakdowns];
                  newBreakdowns[breakdownPickerIndex] = newBreakdown;
                  handleBreakdownsChange(newBreakdowns);
                } else {
                  // Adding new breakdown
                  handleBreakdownsChange([...currentBreakdowns, newBreakdown]);
                }
                setBreakdownPickerOpen(false);
                setBreakdownPickerIndex(null);
              }}
              firstTimeOnly={false} // Exclude first time for breakdowns
              onFirstTimeChange={null} // Not applicable
            />
              </>
            )}

            {/* Retention-only: which funnel step drives the breakdown (event/cohort breakdowns only) */}
            {isRetention &&
              currentBreakdowns.length > 0 &&
              (currentBreakdowns[0]?.propertySource || 'event') !== 'user' && (
                <div className="mt-3 grid grid-cols-1 gap-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs" style={{ color: "var(--text-muted)" }}>Breakdown on</span>
                    <div className="w-[180px]">
                      <InlineDropdown
                        value={currentBreakdowns[0]?.step === 2 ? 2 : 1}
                        onChange={(v) => {
                          const step = Number(v) === 2 ? 2 : 1;
                          const updated = { ...currentBreakdowns[0], step };
                          handleBreakdownsChange([updated]);
                        }}
                        options={[
                          { value: 1, label: 'Step 1' },
                          { value: 2, label: 'Step 2' },
                        ]}
                        widthClass="min-w-[180px]"
                      />
                    </div>
                  </div>
                </div>
              )}
          </>
        )}

        {activeTab === "chart" && (
          <>
            {/* AXIS */}
            <div className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "var(--text-muted)" }}>
              Axis
            </div>

            {/* Left Y-Axis */}
            <div className="mb-6 pb-6 border-b" style={{ borderColor: "var(--border-default)" }}>
              <div className="text-sm font-medium mb-3" style={{ color: "var(--text-primary)" }}>Left Y-Axis</div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Axis</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, visible: true }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.visible ? "var(--brand-primary)" : "var(--surface-base)", color: leftY.visible ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Visible</button>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, visible: false }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: !leftY.visible ? "var(--brand-primary)" : "var(--surface-base)", color: !leftY.visible ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Hidden</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Label</span>
                  <input type="text" value={leftY.label} onChange={(e) => setLeftY((s) => ({ ...s, label: e.target.value }))} placeholder="" className="w-[180px] px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: "var(--surface-base)", borderColor: "var(--border-default)", color: "var(--text-primary)" }} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Unit</span>
                  <div className="w-[180px] flex flex-col gap-1">
                    <div className="flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                      {["$", "%", "#", "Custom"].map((u, index) => (
                        <button key={u} type="button" onClick={() => setLeftY((s) => ({ ...s, unit: u }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.unit === u ? "var(--brand-primary)" : "var(--surface-base)", color: leftY.unit === u ? "var(--ink-onBrand)" : "var(--text-muted)", borderRight: index < 3 ? `1px solid var(--border-default)` : "none" }}>{u}</button>
                      ))}
                    </div>
                    {leftY.unit === "Custom" && (
                      <input type="text" value={leftY.customUnit ?? ""} onChange={(e) => setLeftY((s) => ({ ...s, customUnit: e.target.value }))} placeholder="e.g. users" className="w-full px-2 h-7 border rounded-lg text-xs outline-none" style={{ backgroundColor: "var(--surface-base)", borderColor: "var(--border-default)", color: "var(--text-primary)" }} />
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Abbreviation</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, abbreviation: true }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.abbreviation ? "var(--brand-primary)" : "var(--surface-base)", color: leftY.abbreviation ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Yes</button>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, abbreviation: false }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: !leftY.abbreviation ? "var(--brand-primary)" : "var(--surface-base)", color: !leftY.abbreviation ? "var(--ink-onBrand)" : "var(--text-muted)" }}>No</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Decimals</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, decimals: ".0" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.decimals === ".0" ? "var(--brand-primary)" : "var(--surface-base)", color: leftY.decimals === ".0" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>+.0</button>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, decimals: ".00" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.decimals === ".00" ? "var(--brand-primary)" : "var(--surface-base)", color: leftY.decimals === ".00" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>.00 →</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Preview</span>
                  <span className="text-xs font-medium w-[180px]" style={{ color: "var(--text-primary)" }}>
                    {(() => {
                      const v = 1250000;
                      const dp = leftY.decimals === ".00" ? 2 : 0;
                      const u = leftY.unit ?? "#";
                      const cu = leftY.customUnit ?? "";
                      const abbr = leftY.abbreviation !== false;
                      const pre = u === "$" ? "$" : "";
                      const suf = u === "%" ? "%" : u === "Custom" ? (cu || "unit") : "";
                      if (abbr) return `${pre}1.25M${suf}`;
                      return `${pre}${v.toLocaleString(undefined, { minimumFractionDigits: dp })}${suf}`;
                    })()}
                  </span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Threshold Bounds</span>
                  <div className="w-[180px] grid grid-cols-2 gap-2">
                    <div>
                      <input type="text" value={leftY.thresholdMin} onChange={(e) => setLeftY((s) => ({ ...s, thresholdMin: e.target.value }))} placeholder="Min" className="w-full px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: "var(--surface-base)", borderColor: "var(--border-default)", color: "var(--text-primary)" }} />
                    </div>
                    <div>
                      <input type="text" value={leftY.thresholdMax} onChange={(e) => setLeftY((s) => ({ ...s, thresholdMax: e.target.value }))} placeholder="Max" className="w-full px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: "var(--surface-base)", borderColor: "var(--border-default)", color: "var(--text-primary)" }} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs flex items-center gap-1" style={{ color: "var(--text-muted)" }}>Out of Bounds <Info className="w-3 h-3" /></span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, outOfBounds: "visible" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.outOfBounds === "visible" ? "var(--brand-primary)" : "var(--surface-base)", color: leftY.outOfBounds === "visible" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Visible</button>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, outOfBounds: "hidden" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.outOfBounds === "hidden" ? "var(--brand-primary)" : "var(--surface-base)", color: leftY.outOfBounds === "hidden" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Hidden</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Scale</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, scale: "linear" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.scale === "linear" ? "var(--brand-primary)" : "var(--surface-base)", color: leftY.scale === "linear" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Linear</button>
                    <button type="button" onClick={() => setLeftY((s) => ({ ...s, scale: "log" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: leftY.scale === "log" ? "var(--brand-primary)" : "var(--surface-base)", color: leftY.scale === "log" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Logarithmic</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Y-Axis */}
            <div className="mb-6 pb-6 border-b" style={{ borderColor: "var(--border-default)" }}>
              <div className="text-sm font-medium mb-3" style={{ color: "var(--text-primary)" }}>Right Y-Axis</div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Axis</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, visible: true }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.visible ? "var(--brand-primary)" : "var(--surface-base)", color: rightY.visible ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Visible</button>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, visible: false }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: !rightY.visible ? "var(--brand-primary)" : "var(--surface-base)", color: !rightY.visible ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Hidden</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Label</span>
                  <input type="text" value={rightY.label} onChange={(e) => setRightY((s) => ({ ...s, label: e.target.value }))} className="w-[180px] px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: "var(--surface-base)", borderColor: "var(--border-default)", color: "var(--text-primary)" }} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Unit</span>
                  <div className="w-[180px] flex flex-col gap-1">
                    <div className="flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                      {["$", "%", "#", "Custom"].map((u, index) => (
                        <button key={u} type="button" onClick={() => setRightY((s) => ({ ...s, unit: u }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.unit === u ? "var(--brand-primary)" : "var(--surface-base)", color: rightY.unit === u ? "var(--ink-onBrand)" : "var(--text-muted)", borderRight: index < 3 ? `1px solid var(--border-default)` : "none" }}>{u}</button>
                      ))}
                    </div>
                    {rightY.unit === "Custom" && (
                      <input type="text" value={rightY.customUnit ?? ""} onChange={(e) => setRightY((s) => ({ ...s, customUnit: e.target.value }))} placeholder="e.g. users" className="w-full px-2 h-7 border rounded-lg text-xs outline-none" style={{ backgroundColor: "var(--surface-base)", borderColor: "var(--border-default)", color: "var(--text-primary)" }} />
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Abbreviation</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, abbreviation: true }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.abbreviation ? "var(--brand-primary)" : "var(--surface-base)", color: rightY.abbreviation ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Yes</button>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, abbreviation: false }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: !rightY.abbreviation ? "var(--brand-primary)" : "var(--surface-base)", color: !rightY.abbreviation ? "var(--ink-onBrand)" : "var(--text-muted)" }}>No</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Decimals</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, decimals: ".0" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.decimals === ".0" ? "var(--brand-primary)" : "var(--surface-base)", color: rightY.decimals === ".0" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>+.0</button>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, decimals: ".00" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.decimals === ".00" ? "var(--brand-primary)" : "var(--surface-base)", color: rightY.decimals === ".00" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>.00 →</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Preview</span>
                  <span className="text-xs font-medium w-[180px]" style={{ color: "var(--text-primary)" }}>
                    {(() => {
                      const v = 1250000;
                      const dp = rightY.decimals === ".00" ? 2 : 0;
                      const u = rightY.unit ?? "#";
                      const cu = rightY.customUnit ?? "";
                      const abbr = rightY.abbreviation !== false;
                      const pre = u === "$" ? "$" : "";
                      const suf = u === "%" ? "%" : u === "Custom" ? (cu || "unit") : "";
                      if (abbr) return `${pre}1.25M${suf}`;
                      return `${pre}${v.toLocaleString(undefined, { minimumFractionDigits: dp })}${suf}`;
                    })()}
                  </span>
                </div>
                <div className="flex items-start justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Threshold Bounds</span>
                  <div className="w-[180px] grid grid-cols-2 gap-2">
                    <div>
                      <input type="text" value={rightY.thresholdMin} onChange={(e) => setRightY((s) => ({ ...s, thresholdMin: e.target.value }))} placeholder="Min" className="w-full px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: "var(--surface-base)", borderColor: "var(--border-default)", color: "var(--text-primary)" }} />
                    </div>
                    <div>
                      <input type="text" value={rightY.thresholdMax} onChange={(e) => setRightY((s) => ({ ...s, thresholdMax: e.target.value }))} placeholder="Max" className="w-full px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: "var(--surface-base)", borderColor: "var(--border-default)", color: "var(--text-primary)" }} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs flex items-center gap-1" style={{ color: "var(--text-muted)" }}>Out of Bounds <Info className="w-3 h-3" /></span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, outOfBounds: "visible" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.outOfBounds === "visible" ? "var(--brand-primary)" : "var(--surface-base)", color: rightY.outOfBounds === "visible" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Visible</button>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, outOfBounds: "hidden" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.outOfBounds === "hidden" ? "var(--brand-primary)" : "var(--surface-base)", color: rightY.outOfBounds === "hidden" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Hidden</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Scale</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, scale: "linear" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.scale === "linear" ? "var(--brand-primary)" : "var(--surface-base)", color: rightY.scale === "linear" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Linear</button>
                    <button type="button" onClick={() => setRightY((s) => ({ ...s, scale: "log" }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: rightY.scale === "log" ? "var(--brand-primary)" : "var(--surface-base)", color: rightY.scale === "log" ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Logarithmic</button>
                  </div>
                </div>
              </div>
            </div>

            {/* X-Axis */}
            <div className="mb-6 pb-6 border-b" style={{ borderColor: "var(--border-default)" }}>
              <div className="text-sm font-medium mb-3" style={{ color: "var(--text-primary)" }}>X-Axis</div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Axis</span>
                  <div className="w-[180px] flex border overflow-hidden rounded-lg" style={{ borderColor: "var(--border-default)" }}>
                    <button type="button" onClick={() => setXAxis((s) => ({ ...s, visible: true }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: xAxis.visible ? "var(--brand-primary)" : "var(--surface-base)", color: xAxis.visible ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Visible</button>
                    <button type="button" onClick={() => setXAxis((s) => ({ ...s, visible: false }))} className="flex-1 px-2 h-7 text-xs transition-colors flex items-center justify-center" style={{ backgroundColor: !xAxis.visible ? "var(--brand-primary)" : "var(--surface-base)", color: !xAxis.visible ? "var(--ink-onBrand)" : "var(--text-muted)" }}>Hidden</button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: "var(--text-muted)" }}>Label</span>
                  <input type="text" value={xAxis.label} onChange={(e) => setXAxis((s) => ({ ...s, label: e.target.value }))} className="w-[180px] px-2 h-7 border rounded-lg text-sm outline-none" style={{ backgroundColor: "var(--surface-base)", borderColor: "var(--border-default)", color: "var(--text-primary)" }} />
                </div>
              </div>
            </div>

            {/* Metric Axis Assignment - L/R per metric; for funnels show funnel steps as one series (all funnel chart types) */}
            <div className="mb-6 mt-4 border-b pb-6" style={{ borderColor: "var(--border-default)" }}>
              <div className="mb-2 text-sm font-medium text-ink-muted">Axis assignment</div>
              {isFunnel && funnelSteps.length > 0 ? (
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 flex-1 truncate text-xs font-medium leading-tight" style={{ color: "var(--text-primary)" }} title={funnelSteps.map((s) => s.label || s.eventName || "Step").join(" through ") || "Funnel steps"}>
                      {(() => {
                        const funnelLabel = funnelSteps.map((s) => s.label || s.eventName || "Step").join(" through ") || "Funnel steps";
                        return funnelLabel.length > 28 ? funnelLabel.slice(0, 28) + "…" : funnelLabel;
                      })()}
                    </span>
                    <div className="flex shrink-0 overflow-hidden rounded-md border" style={{ borderColor: "var(--border-default)" }}>
                      <button
                        type="button"
                        onClick={() => setFunnelAxis("left")}
                        className="h-6 px-2.5 text-[11px] font-medium transition-colors"
                        style={{ backgroundColor: funnelAxis === "left" ? "var(--brand-primary)" : "var(--surface-base)", color: funnelAxis === "left" ? "var(--ink-onBrand)" : "var(--text-muted)" }}
                      >
                        L
                      </button>
                      <button
                        type="button"
                        onClick={() => setFunnelAxis("right")}
                        className="h-6 px-2.5 text-[11px] font-medium transition-colors"
                        style={{ backgroundColor: funnelAxis === "right" ? "var(--brand-primary)" : "var(--surface-base)", color: funnelAxis === "right" ? "var(--ink-onBrand)" : "var(--text-muted)" }}
                      >
                        R
                      </button>
                    </div>
                  </div>
                </div>
              ) : ((currentMetrics || []).filter((m) => m?.events?.length > 0).length > 0) ? (
                <div className="space-y-2">
                  {(currentMetrics || [])
                    .map((metric, origIndex) => ({ metric, origIndex }))
                    .filter(({ metric: m }) => m?.events?.length > 0)
                    .map(({ metric, origIndex }) => {
                      const label = metric.events?.map((e) => e.name).filter(Boolean).join(" + ") || `Metric ${String.fromCharCode(65 + origIndex)}`;
                      const axis = metric.axis || "left";
                      return (
                        <div key={metric.id || origIndex} className="flex items-center justify-between gap-2">
                          <span className="text-sm truncate flex-1 min-w-0" style={{ color: "var(--text-primary)" }} title={label}>
                            {label.length > 24 ? label.slice(0, 24) + "…" : label}
                          </span>
                          <div className="flex border overflow-hidden rounded-lg shrink-0" style={{ borderColor: "var(--border-default)" }}>
                            <button
                              type="button"
                              onClick={() => handleUpdateMetric(origIndex, { ...metric, axis: "left" })}
                              className="px-3 h-7 text-xs font-medium transition-colors"
                              style={{ backgroundColor: axis === "left" ? "var(--brand-primary)" : "var(--surface-base)", color: axis === "left" ? "var(--ink-onBrand)" : "var(--text-muted)" }}
                            >
                              L
                            </button>
                            <button
                              type="button"
                              onClick={() => handleUpdateMetric(origIndex, { ...metric, axis: "right" })}
                              className="px-3 h-7 text-xs font-medium transition-colors"
                              style={{ backgroundColor: axis === "right" ? "var(--brand-primary)" : "var(--surface-base)", color: axis === "right" ? "var(--ink-onBrand)" : "var(--text-muted)" }}
                            >
                              R
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="px-4 py-6 border border-dashed rounded-lg text-center text-sm" style={{ borderColor: "var(--border-default)", color: "var(--text-muted)", lineHeight: 1.5 }}>
                  <span className="block">Add metrics in the Query tab, then assign each to</span>
                  <span className="block font-medium mt-0.5">Left (L) or Right (R) axis</span>
                </div>
              )}
            </div>

            {/* THEMES */}
            <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>Themes</div>
            <div className="mb-2">
              <div className="flex items-center gap-2 px-2 h-7 border rounded-lg" style={{ backgroundColor: "var(--surface-base)", borderColor: "var(--border-default)" }}>
                <Search className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
                <input type="text" value={themeSearch} onChange={(e) => setThemeSearch(e.target.value)} placeholder="Find a theme" className="flex-1 min-w-0 bg-transparent text-sm outline-none" style={{ color: "var(--text-primary)" }} />
              </div>
            </div>
            <div className="space-y-1.5">
              {CHART_THEMES.filter((t) => !themeSearch || t.label.toLowerCase().includes(themeSearch.toLowerCase())).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedThemeId(t.id)}
                  className="w-full flex items-center gap-3 px-2 py-2 border rounded-lg text-left transition-colors"
                  style={{
                    borderColor: selectedThemeId === t.id ? "var(--brand-primary)" : "var(--border-default)",
                    backgroundColor: selectedThemeId === t.id ? "var(--surface-hover)" : "transparent",
                    color: "var(--text-primary)",
                  }}
                >
                  <span className="flex gap-0.5 flex-shrink-0">
                    {t.colors.map((c, i) => (
                      <span key={i} className="w-4 h-4" style={{ backgroundColor: c }} />
                    ))}
                  </span>
                  <span className="text-sm truncate">{t.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

      </div>

      {/* Global Unified Filter Module */}
      {isGlobalFilterOpen && (
        <GlobalPropertyFilterModule
          isOpen={isGlobalFilterOpen}
          onClose={() => {
            setIsGlobalFilterOpen(false);
            setSelectedGlobalFilterForEdit(null);
          }}
          // Always anchor the popup to the same "+ Filter" button used when
          // opening the selector the first time, so editing a filter opens
          // the selector in exactly the same position as initial create.
          triggerRef={globalFilterButtonRef}
          teamId={teamId}
          existingFilter={selectedGlobalFilterForEdit}
          onGlobalFilterAdd={handleGlobalFiltersChange}
          firstTimeOnly={!!globalFirstTimeFilter}
          onFirstTimeChange={handleGlobalFirstTimeChange}
        />
      )}

    </aside>
  );
}
